/** The words Conversations puts on times: a line's stamp, a day's heading, a list row, a file's week. All in the app's zone. */
import { dayNumber, formatAt } from "../lib/zones";

const ms = (at: number) => at * 1000;
const daysAgo = (at: number, now: number) => dayNumber(now) - dayNumber(ms(at));

export function clockTime(at: number): string {
  return formatAt(ms(at), { hour: "numeric", minute: "2-digit" });
}

/** "Tue 20 Oct": a day as the rooms name it. */
export function dateWords(at: number): string {
  return `${formatAt(ms(at), { weekday: "short" })} ${formatAt(ms(at), { day: "numeric" })} ${formatAt(ms(at), { month: "short" })}`;
}

/** A line's stamp: "Today 9:00 AM", "Wed 9:10 AM" inside a week, then "Mon 19 Oct". */
export function lineWhen(at: number, now = Date.now()): string {
  const days = daysAgo(at, now);
  if (days <= 0) return `Today ${clockTime(at)}`;
  if (days < 7) return `${formatAt(ms(at), { weekday: "short" })} ${clockTime(at)}`;
  return dateWords(at);
}

/** A post's stamp in a list of posts: "9:00 AM" today, "Thu 4:00 PM" inside a week, then "Mon 19 Oct". */
export function postWhen(at: number, now = Date.now()): string {
  return daysAgo(at, now) <= 0 ? clockTime(at) : lineWhen(at, now);
}

/** The heading over each day's first line: "Today", "Yesterday", "Wednesday", then "Tuesday 20 October". */
export function dayLabel(at: number, now = Date.now()): string {
  const days = daysAgo(at, now);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return formatAt(ms(at), { weekday: "long" });
  return `${formatAt(ms(at), { weekday: "long" })} ${formatAt(ms(at), { day: "numeric" })} ${formatAt(ms(at), { month: "long" })}`;
}

/** A rail row's time: "Now", "14 min", "2 h", the weekday, then the date. */
export function listTime(at: number, now = Date.now()): string {
  const mins = Math.floor((now / 1000 - at) / 60);
  if (mins < 1) return "Now";
  if (mins < 60) return `${mins} min`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)} h`;
  if (mins < 7 * 24 * 60) return formatAt(ms(at), { weekday: "short" });
  return formatAt(ms(at), { day: "numeric", month: "short" });
}

/** How long ago the person was on their page: "2 hours ago", "3 days ago". */
export function agoWords(at: number, now = Date.now()): string {
  const mins = Math.max(0, Math.floor((now / 1000 - at) / 60));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"} ago`;
}

/** The week a file was shared in, for the headings of Files. */
export function weekOf(at: number, now = Date.now()): string {
  const days = daysAgo(at, now);
  if (days < 7) return "This week";
  if (days < 14) return "Last week";
  return formatAt(ms(at), { month: "long", year: "numeric" });
}

/** "Today", else "Thu 22 Oct": a file's day. */
export function shortDay(at: number, now = Date.now()): string {
  return daysAgo(at, now) <= 0 ? "Today" : dateWords(at);
}

/** "Monday 2 November": the day of a call. */
export function callDay(at: number): string {
  return `${formatAt(ms(at), { weekday: "long" })} ${formatAt(ms(at), { day: "numeric" })} ${formatAt(ms(at), { month: "long" })}`;
}

/** "Mon 2 Nov, 12:00 PM": the call as one line under a name. */
export function callLine(at: number): string {
  return `${dateWords(at)}, ${clockTime(at)}`;
}

/** "4 min": how long a call has been going. */
export function sinceWords(at: number, now = Date.now()): string {
  const mins = Math.max(0, Math.floor((now / 1000 - at) / 60));
  return mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)} h ${mins % 60} min`;
}

export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

/** "Priya", "Priya and Noah", "Priya, Noah and 2 more": who is typing. */
export function typingWords(names: string[]): string {
  const firsts = names.map(firstName);
  if (firsts.length <= 1) return `${firsts[0] ?? ""} is typing`;
  if (firsts.length === 2) return `${firsts[0]} and ${firsts[1]} are typing`;
  return `${firsts[0]}, ${firsts[1]} and ${firsts.length - 2} more are typing`;
}

/** "Thu, Nov 12": the day a pin leaves on. */
export function pinDay(at: number): string {
  return formatAt(ms(at), { weekday: "short", month: "short", day: "numeric" });
}

/** A date picked in the sheet ("2026-11-12") as the moment that day ends, in Unix seconds. */
export function endOfDay(date: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return null;
  return Math.floor(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59).getTime() / 1000);
}

/** A date input's value for a day some days from now. */
export function dateIn(days: number, now = Date.now()): string {
  const d = new Date(now + days * 86_400_000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** "1 person", "31 people". */
export const peopleWords = (n: number) => `${n.toLocaleString("en-US")} ${n === 1 ? "person" : "people"}`;

/** "The 1 person is told at once, on their page and by email, and joins from the circle." with the verbs that fit the count. */
export const toldWords = (n: number, phone: boolean): string =>
  `The ${peopleWords(n)} ${n === 1 ? "is" : "are"} told at once, on their page and by email${phone ? "." : `, and ${n === 1 ? "joins" : "join"} from the circle.`}`;

/** "1 is coming", "3 are coming": a count that carries its own verb. */
export const comingWords = (n: number) => (n === 1 ? "1 is coming" : `${n.toLocaleString("en-US")} are coming`);

/** Where a circle's people came from, in a line: a group says "Made from ...", picked people already say "Chosen from ...". */
export const madeWords = (words: string): string => (/^chosen from/i.test(words) ? words : `Made from ${words}`);
